import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { mapTicketTotal, mapTicketsResponseToDto } from '../dto/support-tickets-list.dto.js';
import { buildTicketsTableModel } from '../model/support-tickets-list.model.js';

export function useSupportTicketsList() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    statuses: [],
    assignedOnly: false,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const payload = {
        sortOrder: 'desc',
      };

      if (filters.statuses.length > 0) {
        payload.statuses = filters.statuses;
      }

      if (filters.assignedOnly) {
        payload.assignedOnly = true;
      }

      const response = await adminApi.getSupportTickets(token, payload);
      const dto = mapTicketsResponseToDto(response);
      setRows(buildTicketsTableModel(dto));
      setTotal(mapTicketTotal(response));
    } catch (requestError) {
      setRows([]);
      setError(requestError.message || 'Failed to load tickets');
    } finally {
      setIsLoading(false);
    }
  }, [token, filters.statuses, filters.assignedOnly]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const toggleStatus = (status) => {
    setFilters((prev) => {
      const has = prev.statuses.includes(status);
      return {
        ...prev,
        statuses: has ? prev.statuses.filter((s) => s !== status) : [...prev.statuses, status],
      };
    });
  };

  const resetFilters = () => {
    setFilters({ statuses: [], assignedOnly: false });
    setIsFilterOpen(false);
  };

  return {
    rows,
    total,
    isLoading,
    error,
    filters,
    isFilterOpen,
    setIsFilterOpen,
    toggleStatus,
    resetFilters,
    onAssignedOnlyChange: (value) => setFilters((prev) => ({ ...prev, assignedOnly: value })),
    reload: loadTickets,
  };
}
